import { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Button,
  IconButton
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TestRunsTable from './TestRunsTable'
import { formatVersionLabel } from '../../utils/formatVersionLabel'
import ArrowBackSharpIcon from '@mui/icons-material/ArrowBackSharp'

const RunsAccordion = ({
  testCase,
  testRuns,
  compact = false,
  embedded = false,
  onReviewClick,
  onBack,
  onRunSelect = null,
  showReviewButton = true,
}) => {
  const [selectedVersion, setSelectedVersion] = useState('All Versions')

  const latestRun = useMemo(() => {
    if (!Array.isArray(testRuns) || !testRuns.length) return null
    return [...testRuns].sort(
      (a, b) => new Date(b.updated_at || b.started_at || 0) - new Date(a.updated_at || a.started_at || 0)
    )[0]
  }, [testRuns])

  const handleReviewClick = () => {
    if (typeof onReviewClick === 'function') {
      onReviewClick(latestRun)
      return
    }
    if (!latestRun?.id) return
    const url = `${window.location.origin}/test-run/${latestRun.id}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (embedded) {
    return (
      <Box className='runsAccordionContainer'>
        <Box
          className='runsAccordionSummary'
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
        >
          {onBack ? (
            <IconButton
              size="small"
              onClick={onBack}
              sx={{
                border: '1px solid #2D4356',
                backgroundColor: '#fff',
                color: '#2D4356',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: '#f5f7fb',
                },
              }}
            >
              <ArrowBackSharpIcon fontSize="small" />
            </IconButton>
          ) : null}
          <Typography variant="h5" sx={{ mt: 2 }} className='runsAccordionSummaryTitle'>Test Runs</Typography>
        </Box>
        { testCase && testCase.versions.length > 1 ?
          <Box className="runsAccordionVersionsContainer">
            <FormControl>
              <InputLabel>Versions</InputLabel>
              <Select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                displayEmpty
                size="small"
                label="Versions"
                sx={{ width: '340px', maxWidth: '340px' }}
              >
                <MenuItem value="All Versions">All Versions</MenuItem>
                {testCase.versions.map((version) => (
                  <MenuItem key={version.id} value={version.id}>
                    {formatVersionLabel(version.id, testCase.versions)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> : null
        }
        <TestRunsTable
          testCase={testCase}
          testRuns={testRuns}
          selectedVersion={selectedVersion}
          onRunSelect={onRunSelect}
        />
      </Box>
    )
  }

  if (compact) {
    return (
      <Box className='runsAccordionContainer' sx={{ marginBottom: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleReviewClick}
          disabled={!latestRun}
          sx={{ ml: 1, display: showReviewButton ? 'inline-flex' : 'none' }}
        >
          Review Test Runs
        </Button>
        {!latestRun ? (
          <Typography sx={{ mt: 1 }} color="text.secondary">
            No test runs available yet.
          </Typography>
        ) : null}
      </Box>
    )
  }

  return <Box className='runsAccordionContainer'>
    <Accordion className='runsAccordion' defaultExpanded>
      <AccordionSummary className='runsAccordionSummary' expandIcon={<ExpandMoreIcon/>}>
        <Typography variant="h5" className='runsAccordionSummaryTitle'>Test Runs</Typography>
      </AccordionSummary>
      <AccordionDetails className='runsAccordionContent'>
        <>
          { testCase && testCase.versions.length > 1 ?
            <Box className="runsAccordionVersionsContainer">
              <FormControl>
                <InputLabel>Versions</InputLabel>
                <Select
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                  displayEmpty
                  size="small"
                  label="Versions"
                  sx={{ width: '340px', maxWidth: '340px' }}
                >
                  <MenuItem value="All Versions">All Versions</MenuItem>
                  {testCase.versions.map((version) => (
                    <MenuItem key={version.id} value={version.id}>
                      {formatVersionLabel(version.id, testCase.versions)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box> : null
          }
          <TestRunsTable
            testCase={testCase}
            testRuns={testRuns}
            selectedVersion={selectedVersion}
            onRunSelect={onRunSelect}
          />
        </>
      </AccordionDetails>
    </Accordion>
  </Box>
}

export default RunsAccordion
